import { useDispatch } from "react-redux";
import { toast } from "sonner";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { openStatusAuditInNewTab } from "@/utils/statusAuditNavigation";
import { openShipmentDocumentInNewTab } from "@/utils/shipmentDocumentNavigation";
import {
  Eye,
  Printer,
  Upload,
  Mail,
  Trash2,
  ArrowRight,
  Calendar,
  User,
  Zap,
  Edit3,
  Ship,
  RotateCcw,
  FileText,
  MoreHorizontal,
} from "lucide-react";
import Stepper from "./Stepper";
import { InlineCountryFlag } from "./CountryBadge";
const { getBookingDetails } = require("@/redux/Booking/actions");
const ExpandedDetails = require("../booking/ExpandedDetails").default;
const StatusBadge = require("./StatusBadge").default;
const DocumentIcons = require("./DocumentIcons").default;

const STEPS = [
  "Booked",
  "Gated",
  "Loaded",
  "On Vessel",
  "Arrived",
  "Delivered",
];

// ─── Icon Button ──────────────────────────────────────────────────────────────
function IconBtn({ icon: Icon, label, tone = "default", onClick }) {
  const toneCls = {
    default:
      "text-gray-500 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-100 dark:hover:bg-gray-700",
    danger:
      "text-rose-500 hover:text-rose-700 hover:bg-rose-50 dark:text-rose-400 dark:hover:text-rose-300 dark:hover:bg-rose-900/30",
    primary:
      "text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:text-blue-400 dark:hover:text-blue-300 dark:hover:bg-blue-900/30",
  }[tone];

  return (
    <button
      title={label}
      onClick={onClick}
      className={`inline-flex items-center justify-center w-8 h-8 rounded-md transition-colors ${toneCls}`}
    >
      <Icon className="w-4 h-4" />
    </button>
  );
}

// ─── Label / Value pair ───────────────────────────────────────────────────────
function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">
        {label}
      </div>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}

// ─── Port country helper ──────────────────────────────────────────────────────
const PORT_COUNTRY_MAP = {
  USORF: "US",
  USCHI: "US",
  USLAX: "US",
  USNYC: "US",
  USSEA: "US",
  INNSA: "IN",
  INMUN: "IN",
  INMAA: "IN",
  INBOM: "IN",
  CNSHA: "CN",
  CNNGB: "CN",
  CNSZX: "CN",
  CNTAO: "CN",
  SGSIN: "SG",
  AEDXB: "AE",
  GBFXT: "GB",
  NLRTM: "NL",
  DEHAM: "DE",
  FRLEH: "FR",
  JPYOK: "JP",
  KRPUS: "KR",
  AUSYD: "AU",
  CAVAN: "CA",
  BRSSZ: "BR",
  MXZLO: "MX",
};

function getCountryCodeByPort(portCode) {
  if (!portCode || typeof portCode !== "string") return null;
  const code = portCode.trim().toUpperCase();
  return PORT_COUNTRY_MAP[code] ?? (code.length >= 2 ? code.slice(0, 2) : null);
}

function getLocationName(location) {
  if (!location) return "N/A";
  return location
    .split(",")[0]
    .replace(/\[.*\]/g, "")
    .trim();
}

function formatDate(dateStr) {
  if (!dateStr) return "-";
  try {
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

// ─── Shipment type pill colours ───────────────────────────────────────────────
const TYPE_PILL = {
  FCL: "bg-blue-50 text-blue-700 ring-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:ring-blue-700",
  LCL: "bg-violet-50 text-violet-700 ring-violet-200 dark:bg-violet-900/30 dark:text-violet-300 dark:ring-violet-700",
  RORO: "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:ring-amber-700",
};

// ─── Main Component ───────────────────────────────────────────────────────────
const ShipmentCard = ({
  shipment,
  onView,
  onPrint,
  onEmail,
  onDelete,
  onOpenNotes,
  onOpenFileUpload,
  expand,
  setExpand,
  isImport = false,
  isDeleted = false,
  setRestoreDeletePopup = () => { },
}) => {
  const dispatch = useDispatch();
  const [charges, setCharges] = useState(null);

  const originCode =
    getCountryCodeByPort(shipment?.portOfLoadingCode) ??
    shipment?.portOfLoading?.slice(0, 2) ??
    "US";
  const destCode =
    getCountryCodeByPort(shipment?.portOfDischargeCode) ??
    shipment?.portOfDischarge?.slice(0, 2) ??
    "CN";

  const status = shipment?.status ?? "Pending Onboard";
  const shipmentType = shipment?.shippingType ?? "FCL";
  const progressStep = shipment?.progress ?? 5;
  const typePillCls = TYPE_PILL[shipmentType] ?? TYPE_PILL.FCL;

  // Fetch charges when expanded (export only)
  useEffect(() => {
    if (!expand || expand !== shipment?.id) return;
    (async () => {
      try {
        const result = await dispatch(getBookingDetails(expand));
        setCharges(result?.rates ?? []);
      } catch {
        toast.error("Failed to load booking details");
      }
    })();
  }, [expand, dispatch, shipment?.id]);

  const handleExpand = (id) => setExpand(id === expand ? "" : id);

  const handleAction = (e, type) => {
    e.stopPropagation();
    openShipmentDocumentInNewTab(type, shipment);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="group relative bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800
                 rounded-xl shadow-sm hover:shadow-lg hover:-translate-y-0.5
                 transition-all duration-200 ease-out overflow-hidden"
    >
      {/* ── Header ── */}
      <header
        className="px-5 pt-4 pb-3 flex flex-wrap items-start justify-between gap-3
                         border-b border-gray-100 dark:border-gray-800"
      >
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            {/* Carrier booking number — always clickable */}
            <span
              className="text-[14px] font-bold text-gray-900 dark:text-gray-50 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors  "
              onClick={() => onView(shipment?.id)}
            >
              {shipment?.carrierBookingNumber ?? "SHZ00000000"} 🔗
            </span>

            <span className="text-gray-400">·</span>

            {/* File number — HBL link for export only, plain text for import */}
            {isImport ? (
              <span className="text-[14px] font-bold text-gray-700 dark:text-gray-300  font-mono font-extrabold select-text">
                {shipment?.fileNumber ?? "N/A"}
              </span>
            ) : (
              <span
                className="text-[14px] font-bold text-gray-700 dark:text-gray-300 font-mono font-extrabold cursor-pointer hover:text-blue-600"
                onClick={() => {
                  const url = `/hbl?bookingId=${shipment.id}&FILENUMBER=${shipment.fileNumber}`;
                  window.open(url, "_blank");
                }}
              >
                {shipment?.fileNumber ?? "N/A"} 🔗
              </span>
            )}
          </div>

          <div className="mt-1.5 flex items-center gap-2 text-[12.5px]">
            <InlineCountryFlag code={originCode} />
            <span className="font-semibold text-gray-900 dark:text-gray-100 font-mono">
              {getLocationName(shipment?.portOfLoading ?? "N/A")}
            </span>
            <ArrowRight className="w-3.5 h-3.5 text-gray-400 dark:text-gray-600" />
            <InlineCountryFlag code={destCode} />
            <span className="font-semibold text-gray-900 dark:text-gray-100 font-mono">
              {getLocationName(shipment?.portOfDischarge)}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <span
            className={`inline-flex items-center px-2 h-6 rounded-md text-[10.5px] font-bold ring-1 ${typePillCls}`}
          >
            {shipmentType}
          </span>
          {/* <StatusBadge status={status} /> */}
        </div>
      </header>

      {/* ── Body: 3-col grid ── */}
      <div className="px-5 py-4 grid grid-cols-1 md:grid-cols-3 gap-x-5 gap-y-3">
        {/* Left — Customer */}
        <div className="space-y-3 min-w-0">
          <Field label="Customer">
            <span className="text-[12.5px] font-medium text-gray-900 dark:text-gray-100 truncate block">
              {shipment?.customerName ?? shipment?.customer ?? "—"}
            </span>
          </Field>
          <Field label="Consignee">
            <span className="text-[12.5px] font-medium text-gray-800 dark:text-gray-200 truncate block">
              {shipment?.consignee ?? "—"}
            </span>
            <div className="text-[10.5px] text-gray-500 dark:text-gray-400 mt-0.5">
              Customer PO:{" "}
              <span className="text-gray-700 dark:text-gray-300 font-mono">
                {shipment?.customerReference ?? shipment?.customerPO ?? "N/A"}
              </span>
            </div>
          </Field>
        </div>

        {/* Center — Dates & Carrier */}
        <div className="space-y-3 min-w-0">
          <div className="grid grid-cols-2 gap-3">
            <Field label="ETD">
              <span className="text-[12.5px] font-medium text-gray-900 dark:text-gray-100 inline-flex items-center gap-1">
                <Calendar className="w-3 h-3 text-gray-400 dark:text-gray-500" />
                {formatDate(shipment?.etsAtPol ?? shipment?.etd)}
              </span>
            </Field>
            <Field label="ETA">
              <span className="text-[12.5px] font-medium text-gray-900 dark:text-gray-100 inline-flex items-center gap-1">
                <Calendar className="w-3 h-3 text-gray-400 dark:text-gray-500" />
                {formatDate(shipment?.etaAtPod ?? shipment?.eta)}
              </span>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Carrier">
              <span className="text-[12.5px] font-medium text-gray-900 dark:text-gray-100 inline-flex items-center gap-1">
                <Ship className="w-3 h-3 text-gray-400 dark:text-gray-500 shrink-0" />
                <span className="truncate">{shipment?.carrier ?? "—"}</span>
              </span>
            </Field>
            <Field label="Vessel">
              <span className="text-[12.5px] font-medium text-gray-900 dark:text-gray-100 inline-flex items-center gap-1">
                <Ship className="w-3 h-3 text-gray-400 dark:text-gray-500 shrink-0" />
                <span className="truncate">
                  {shipment?.vessel && shipment.vessel !== "-" ? shipment.vessel : "—"}
                </span>
              </span>
            </Field>
          </div>
        </div>

        {/* Right — File Manager & Cancel Remark */}
        <div className="space-y-3 min-w-0">
          <Field label="File Manager">
            <span className="text-[12.5px] font-medium inline-flex items-center gap-1.5">
              <User className="w-3 h-3 text-gray-400 dark:text-gray-500" />
              {!shipment?.fileManager && !shipment?.operatorName ? (
                <span className="text-gray-400 dark:text-gray-500 italic">
                  Unassigned
                </span>
              ) : (
                <span className="text-gray-900 dark:text-gray-100">
                  {shipment?.fileManager ?? shipment?.operatorName}
                </span>
              )}
            </span>
          </Field>
          <Field label="Cancel Remark">
            <span className="text-[12.5px] font-medium text-gray-500 dark:text-gray-400">
              {shipment?.cancelremark ?? shipment?.cancelRemark ?? "N/A"}
            </span>
          </Field>
        </div>
      </div>

      {/* ── Progress ── */}
      {/* <div className="px-5 pt-1 pb-4">
        <Stepper current={progressStep} />
      </div> */}

      {/* ── Footer ── */}
      <footer
        className="flex flex-wrap items-center justify-between gap-3 px-3 py-2
                         bg-gray-50 dark:bg-gray-800/60
                         border-t border-gray-100 dark:border-gray-800 rounded-b-xl"
      >
        {/* Left — Docs & quick actions */}
        <div className="flex items-center gap-3 flex-wrap">
          {!isImport && (
            <>
              <div className="flex items-center gap-2">
                <DocumentIcons click={handleAction} />
                {/* <span className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">
                Docs
            </span> */}
              </div>
              {/* <div className="h-5 w-px bg-gray-200 dark:bg-gray-700" /> */}
            </>
          )}

          <div className="h-5 w-px bg-gray-200 dark:bg-gray-700" />

          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                openStatusAuditInNewTab({ shipment, isImport });
              }}
              className="flex items-center gap-1.5 px-2 py-1 text-[11px] font-semibold
                         text-teal-600 hover:bg-teal-50 dark:text-teal-400 dark:hover:bg-teal-900/30
                         rounded-md transition-colors"
            >
              <Zap size={12} /> Status Audit
            </button>
          </div>
        </div>

        {/* Center — Icon actions */}
        <div className="flex items-center gap-0.5">
          {/* Eye expand — export only */}
          {!isImport && (
            <IconBtn
              icon={Eye}
              label="View Details"
              tone="primary"
              onClick={(e) => {
                e.stopPropagation();
                handleExpand(shipment?.id);
              }}
            />
          )}
          <IconBtn
            icon={Printer}
            label="Print"
            onClick={(e) => {
              e.stopPropagation();
              onPrint(shipment);
            }}
          />
          {/* Mail — export only */}
          {!isImport && (
            <IconBtn
              icon={Mail}
              label="Email"
              tone="primary"
              onClick={(e) => {
                e.stopPropagation();
                onEmail(shipment);
              }}
            />
          )}
          <span className="mx-1 h-5 w-px bg-gray-200 dark:bg-gray-700" />
          <IconBtn
            icon={Upload}
            label="Upload"
            onClick={(e) => {
              e.stopPropagation();
              onOpenFileUpload(shipment);
            }}
          />
          <IconBtn
            icon={Edit3}
            label="Notes"
            onClick={(e) => {
              e.stopPropagation();
              onOpenNotes(shipment);
            }}
          />
          <span className="mx-1 h-5 w-px bg-gray-200 dark:bg-gray-700" />
          {/* <IconBtn icon={FileText} label="Documents"
            onClick={(e) => { e.stopPropagation(); }} /> */}


          {/* Right — Open & Delete/Restore */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => onView(shipment?.id)}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-[11.5px] font-semibold
                       text-blue-700 bg-blue-50 hover:bg-blue-100
                       dark:text-blue-300 dark:bg-blue-900/30 dark:hover:bg-blue-900/50
                       transition-colors"
            >
              Open <ArrowRight className="w-3 h-3" />
            </button>
            <IconBtn
              icon={isDeleted ? RotateCcw : Trash2}
              label={isDeleted ? "Restore" : "Cancel"}
              tone="danger"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(shipment);
              }}
            />
          </div>
        </div>
      </footer>

      {/* ── Expanded Row — export only ── */}
      {!isImport && (
        <AnimatePresence initial={false}>
          {expand === shipment?.id && (
            <motion.div
              key="expanded"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25, ease: "easeInOut" }}
            >
              <ExpandedDetails
                type="shipmentlist"
                shipment={shipment}
                setExpandedRow={setExpand}
                charges={charges}
                setFileUploadVisible={() => { }}
                setSelectedOrderId={() => { }}
                onPrintShipment={() => { }}
                onOpenMail={onEmail}
              />
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </motion.div>
  );
};

export default ShipmentCard;
